// Selesaikan Manual oleh Owner (Offline) — menu Tindakan Owner, konfirmasi berlapis, riwayat & Buka Kembali.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MoreHorizontal, X, ShieldCheck, RotateCcw, AlertTriangle } from "lucide-react";
import "./owner_manual.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/owner-manual`;
const cfg = { withCredentials: true, timeout: 20000 };
const fmt = (iso) => iso ? new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " WIB" : "-";
const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || "Gagal memproses. Tidak ada status yang diubah."; };
const MODE_INFO = {
  shella: ["Masuk Antrean Shella", "Barang sudah selesai dikerjakan tetapi tertahan di sistem. Shella tetap menerima fisik seperti biasa."],
  selesai: ["Langsung Selesai", "Owner sudah memastikan barang selesai secara fisik. Nota bisa langsung Selesai tanpa penerimaan Shella."],
  admin_close: ["Tutup Administratif", "Pekerjaan tidak dilanjutkan karena alasan administratif. Tidak dihitung produksi selesai."],
};

export function OwnerManualMenu({ orderNumber, onDone }) {
  const [pv, setPv] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("");
  const load = () => axios.get(`${API}/orders/${encodeURIComponent(orderNumber)}`, cfg).then(r => setPv(r.data)).catch(() => setPv(null));
  useEffect(() => { load(); }, [orderNumber]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!pv) return null;
  const close = (changed) => { setView(""); load(); if (changed && onDone) onDone(); };
  return <div className="omx-wrap" data-testid="owner-manual-menu">
    <button type="button" className="omx-trigger" onClick={() => setOpen(v => !v)} aria-expanded={open} data-testid="owner-manual-menu-toggle">
      <MoreHorizontal size={16}/> Tindakan Owner</button>
    {open && <div className="omx-pop" role="menu">
      <button type="button" role="menuitem" onClick={() => { setOpen(false); setView("complete"); }} data-testid="owner-manual-open-complete">
        <ShieldCheck size={14}/> Selesaikan Manual oleh Owner</button>
      <button type="button" role="menuitem" onClick={() => { setOpen(false); setView("history"); }} data-testid="owner-manual-open-history">
        <RotateCcw size={14}/> Riwayat Manual & Buka Kembali{pv.actions.length ? ` (${pv.actions.length})` : ""}</button>
    </div>}
    {view === "complete" && <CompleteDialog pv={pv} onClose={close}/>}
    {view === "history" && <HistoryDialog pv={pv} onClose={close}/>}
  </div>;
}

function Shell({ title, onClose, children, testid }) {
  return <div className="omx-overlay" onClick={() => onClose(false)} data-testid={testid}>
    <div className="omx-dialog" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
      <div className="omx-head"><h3>{title}</h3>
        <button type="button" onClick={() => onClose(false)} aria-label="Tutup" data-testid={`${testid}-close`}><X size={18}/></button></div>
      <div className="omx-body">{children}</div>
    </div>
  </div>;
}

function ProductList({ products, pick, onPick }) {
  return <ul className="omx-prods" data-testid="owner-manual-products">
    {products.map(p => {
      const can = !p.done && !p.blocked;
      return <li key={p.id} className={`omx-prod ${p.done ? "is-done" : ""}`} data-testid={`owner-manual-product-${p.id}`}>
        {onPick && <input type="radio" name="omx-prod" disabled={!can} checked={pick === p.id} onChange={() => onPick(p.id)}
          data-testid={`owner-manual-pick-${p.id}`}/>}
        <div><b>{p.name}</b> · {p.qty} pcs
          <small>Status: {p.done ? (p.manual_label || (p.polosan ? "Polosan (tanpa produksi)" : "Selesai produksi")) : p.stage || "-"}{p.pic ? ` · Operator ${p.pic}` : ""}</small>
          {p.pending.map((t, i) => <small key={i} className="omx-pend">Tertahan: {t}</small>)}
          {p.blocked && <small className="omx-block"><AlertTriangle size={12}/> {p.blocked}</small>}
        </div>
      </li>;
    })}
  </ul>;
}

function effectText(pv, scope, pick, mode) {
  const prods = pv.products.filter(p => !p.polosan);
  const targets = scope === "order" ? prods.filter(p => !p.done) : prods.filter(p => p.id === pick);
  const tids = new Set(targets.map(p => p.id));
  if (!prods.every(p => p.done || tids.has(p.id))) return "Nota tetap dalam proses — produk lain masih dikerjakan.";
  const modes = prods.map(p => tids.has(p.id) ? mode : p.manual_mode);
  if (modes.every(m => m === "admin_close")) return "Nota berstatus Selesai · Ditutup Administratif oleh Owner (bukan penerimaan Shella).";
  if (mode === "selesai" && (scope === "order" || modes.every(m => m === "selesai" || m === "admin_close")))
    return "Nota langsung Selesai — Diselesaikan Manual oleh Owner, tanpa penerimaan Shella.";
  return "Nota masuk antrean Shella: Di Shella (menunggu diterima).";
}

function CompleteDialog({ pv, onClose }) {
  const pending = pv.products.filter(p => !p.done && !p.polosan);
  const [scope, setScope] = useState(pending.length > 1 ? "" : "order");
  const [pick, setPick] = useState("");
  const [mode, setMode] = useState("");
  const [reason, setReason] = useState("");
  const [step, setStep] = useState(1);
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const reqId = useRef(crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const canFinalizeOnly = pending.length === 0 && pv.order_status === "Menunggu Finalisasi";
  const blockedOrder = pv.blockers.length > 0;
  const orderBlocked = scope === "order" && pending.some(p => p.blocked);
  const targets = scope === "order" ? pending : pending.filter(p => p.id === pick);
  const valid = !blockedOrder && scope && mode && reason.trim().length >= 10 && !orderBlocked
    && (scope === "order" ? (pending.length > 0 || (canFinalizeOnly && mode === "selesai")) : !!pick);
  const submit = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/orders/${encodeURIComponent(pv.order_number)}/complete`,
        { scope, product_ids: scope === "product" ? [pick] : [], mode, reason: reason.trim(), request_id: reqId.current }, cfg);
      onClose(true);
    } catch (e) { setErr(errText(e)); setBusy(false); }
  };
  return <Shell title="Selesaikan Manual oleh Owner" onClose={onClose} testid="owner-manual-complete-dialog">
    <div className="omx-id"><b data-testid="owner-manual-nota">{pv.order_number}</b> · {pv.customer_name || "—"}
      <small>Status sebelum: <b data-testid="owner-manual-status-before">{pv.order_status}</b> · Bayar: {pv.payment_status || "-"} · Pickup: {pv.pickup_status || "-"}</small></div>
    {blockedOrder && <div className="omx-err" role="alert" data-testid="owner-manual-blockers">{pv.blockers.map((b, i) => <div key={i}>{b}</div>)}</div>}
    {step === 1 && !blockedOrder && <>
      <h4>1. Apa yang diselesaikan?</h4>
      <div className="omx-seg">
        <label className={scope === "product" ? "on" : ""}><input type="radio" checked={scope === "product"} disabled={!pending.length}
          onChange={() => setScope("product")} data-testid="owner-manual-scope-product"/> Satu produk tertentu</label>
        <label className={scope === "order" ? "on" : ""}><input type="radio" checked={scope === "order"}
          onChange={() => { setScope("order"); setPick(""); }} data-testid="owner-manual-scope-order"/> Seluruh nota ({pending.length} produk tertahan)</label>
      </div>
      <ProductList products={pv.products} pick={pick} onPick={scope === "product" ? setPick : null}/>
      {orderBlocked && <div className="omx-err">Ada produk dengan sub-pekerjaan Bordir terbuka — pilih "Satu produk" atau tangani di panel Bordir.</div>}
      {canFinalizeOnly && <div className="omx-note">Semua produk sudah selesai produksi. Owner hanya bisa memilih "Langsung Selesai" untuk seluruh nota.</div>}
      <h4>2. Tujuan penyelesaian</h4>
      <div className="omx-modes">{Object.entries(MODE_INFO).map(([k, [t, d]]) =>
        <label key={k} className={`omx-mode ${mode === k ? "on" : ""} ${k === "admin_close" ? "is-admin" : ""}`}>
          <input type="radio" checked={mode === k} onChange={() => setMode(k)} disabled={canFinalizeOnly && k !== "selesai"}
            data-testid={`owner-manual-mode-${k}`}/><span><b>{t}</b><small>{d}</small></span></label>)}</div>
      <h4>3. Alasan (wajib, min. 10 karakter)</h4>
      <textarea value={reason} onChange={e => setReason(e.target.value)} maxLength={500} rows={3}
        placeholder="Contoh: Scan gagal di tahap Press, barang sudah jadi dan dicek Owner" data-testid="owner-manual-reason"/>
      <div className="omx-actions">
        <button type="button" className="soft-btn" onClick={() => onClose(false)} data-testid="owner-manual-cancel">Batal</button>
        <button type="button" className="primary" disabled={!valid} onClick={() => { setStep(2); setSure(false); }} data-testid="owner-manual-next">Lanjut ke Konfirmasi</button>
      </div>
    </>}
    {step === 2 && <div className="omx-confirm" data-testid="owner-manual-confirm">
      <h4>Konfirmasi terakhir</h4>
      <dl>
        <dt>Cakupan</dt><dd>{scope === "order" ? "SELURUH NOTA" : "SATU PRODUK"}</dd>
        <dt>Tindakan</dt><dd className={mode === "admin_close" ? "warn" : ""}>{MODE_INFO[mode][0]}</dd>
        <dt>Produk</dt><dd>{targets.length ? targets.map(p => <div key={p.id}>{p.name} · {p.qty} pcs · dari {p.stage || "-"}</div>) : "Tidak ada produk yang diubah (finalisasi nota saja)"}</dd>
        <dt>Masih tertahan</dt><dd>{targets.flatMap(p => p.pending).join(" · ") || "-"}</dd>
        <dt>Hasil</dt><dd data-testid="owner-manual-effect">{effectText(pv, scope, pick, mode)}</dd>
        <dt>Alasan</dt><dd>{reason.trim()}</dd>
      </dl>
      <p className="omx-note">Tidak mengubah pembayaran, pengambilan pelanggan, status Shopee, maupun produktivitas karyawan. Riwayat Operator sebelumnya tetap tersimpan.</p>
      <label className="omx-sure"><input type="checkbox" checked={sure} onChange={e => setSure(e.target.checked)} data-testid="owner-manual-sure"/>
        Saya Owner dan memastikan tindakan ini benar.</label>
      {err && <div className="omx-err" role="alert" data-testid="owner-manual-error">{err}</div>}
      <div className="omx-actions">
        <button type="button" className="soft-btn" disabled={busy} onClick={() => setStep(1)} data-testid="owner-manual-back">Kembali</button>
        <button type="button" className="primary" disabled={!sure || busy} onClick={submit} data-testid="owner-manual-submit">{busy ? "Memproses…" : "Ya, Jalankan"}</button>
      </div>
    </div>}
  </Shell>;
}

function HistoryDialog({ pv, onClose }) {
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    try { await axios.post(`${API}/actions/${target.id}/reopen`, { reason: reason.trim() }, cfg); onClose(true); }
    catch (e) { setErr({ text: errText(e), conflicts: e?.response?.data?.detail?.conflicts || [] }); setBusy(false); }
  };
  return <Shell title="Riwayat Manual Owner" onClose={onClose} testid="owner-manual-history-dialog">
    <div className="omx-id"><b>{pv.order_number}</b> · {pv.customer_name || "—"} <small>Status sekarang: {pv.order_status}</small></div>
    {pv.actions.length === 0 && <p className="omx-note">Belum ada tindakan manual Owner pada nota ini.</p>}
    <ul className="omx-acts">{pv.actions.map(a => <li key={a.id} className={a.status === "reopened" ? "is-reopened" : ""} data-testid={`owner-manual-action-${a.id}`}>
      <b>{a.label}</b> · {a.scope === "order" ? "seluruh nota" : "satu produk"}
      <small>{a.owner_name} · {fmt(a.at)} · {a.order_status_before} → {a.order_status_after}</small>
      <small>{a.products.map(p => `${p.name} (${p.qty} pcs, dari ${p.stage_before || "-"})`).join(" · ") || "Finalisasi nota"}</small>
      <small>Alasan: {a.reason}</small>
      {a.status === "reopened" ? <small className="omx-pend">Dibuka Kembali oleh {a.reopened_by} · {fmt(a.reopened_at)} · {a.reopen_reason}</small>
        : <button type="button" className="soft-btn small-btn" onClick={() => { setTarget(a); setReason(""); setSure(false); setErr(null); }}
          data-testid={`owner-manual-reopen-${a.id}`}><RotateCcw size={13}/> Buka Kembali</button>}
    </li>)}</ul>
    {target && <div className="omx-confirm" data-testid="owner-manual-reopen-form">
      <h4>Buka Kembali: {target.label}</h4>
      <p className="omx-note">Pekerjaan dikembalikan persis ke kondisi sebelum tindakan (tahap, Operator, jalur paralel, status nota). Jejak penyelesaian manual tetap tersimpan.</p>
      <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} maxLength={500} placeholder="Alasan buka kembali (wajib, min. 10 karakter)"
        data-testid="owner-manual-reopen-reason"/>
      <label className="omx-sure"><input type="checkbox" checked={sure} onChange={e => setSure(e.target.checked)} data-testid="owner-manual-reopen-sure"/> Saya yakin membuka kembali tindakan ini.</label>
      {err && <div className="omx-err" role="alert" data-testid="owner-manual-reopen-error">{err.text}{err.conflicts.map((c, i) => <div key={i}>• {c}</div>)}</div>}
      <div className="omx-actions">
        <button type="button" className="soft-btn" disabled={busy} onClick={() => setTarget(null)}>Batal</button>
        <button type="button" className="primary" disabled={!sure || busy || reason.trim().length < 10} onClick={submit} data-testid="owner-manual-reopen-submit">{busy ? "Memproses…" : "Ya, Buka Kembali"}</button>
      </div>
    </div>}
  </Shell>;
}

export const ownerManualTag = (o) => {
  if (o?.closure_kind === "admin_closed") return "Ditutup Administratif oleh Owner";
  if (o?.finalized_by_kind === "owner_manual") return "Diselesaikan Manual oleh Owner";
  if ((o?.products || []).some(p => p.owner_manual)) return "Ada produk Diselesaikan Manual oleh Owner";
  return "";
};
